import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useGapAnalysisGenerationStore } from "@/stores/gap-analysis-generation-store";
import { useGapAnalysisStore } from "@/stores/gap-analysis-store";
import type { GapAnalysisResult } from "@/components/expand/GapAnalysisPanel";
import type { AuditResult } from "@/types/audit";

interface GenerateOptions {
  max_papers_per_cluster?: number;
  paper_ids?: string[];
}

interface StartResponse {
  job_id: string;
  total_steps: number;
}

interface ActiveJobResponse {
  job_id: string;
  concept_id: string;
  status: string;
  step: number;
  step_label: string;
  total_steps: number;
  audit_id: string | null;
  error: string | null;
}

const STEP_FALLBACK_LABELS: Record<number, string> = {
  0: "Preparing…",
  1: "Clustering papers",
  2: "Analyzing gaps",
  3: "Finalizing",
};

async function consumeAuditStream(
  conceptId: string,
  jobId: string,
  signal: AbortSignal,
  onComplete: (result: AuditResult) => void,
  onError: (msg: string) => void,
): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
  const token = await getAuthToken();
  const url = `${baseUrl}/api/concepts/${conceptId}/clustering/coverage-audit/generate/${jobId}/stream`;
  const store = useGapAnalysisGenerationStore;

  let receivedTerminal = false;
  try {
    for await (const event of streamSSE(
      url,
      { Authorization: `Bearer ${token}` },
      signal,
    )) {
      const data = event.data ? JSON.parse(event.data) : {};

      if (event.event.startsWith("step:")) {
        const [, stepRaw, kind] = event.event.split(":");
        const step = Number(stepRaw);
        // Also pick up `skipped` (clusters already computed) + `done`
        // so the pill reflects step transitions even when the runner
        // takes a fast path (no `start` for that step).
        if (kind === "start" || kind === "skipped" || kind === "done") {
          store
            .getState()
            .setStep(
              step as 0 | 1 | 2 | 3,
              data.label || STEP_FALLBACK_LABELS[step] || "",
            );
        }
        continue;
      }
      if (event.event === "complete") {
        receivedTerminal = true;
        onComplete(data as AuditResult);
        return;
      }
      if (event.event === "error") {
        receivedTerminal = true;
        onError(data.error || "Audit failed");
        return;
      }
      if (event.event === "cancelled") {
        receivedTerminal = true;
        store.getState().reset();
        // Also clear the legacy store so the panel's "Auditing…" UI
        // unsticks — same reason as the OperationBar pill cancel.
        useGapAnalysisStore.getState().cancel();
        toast.info("Audit cancelled");
        return;
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    onError(err instanceof Error ? err.message : "SSE failed");
    return;
  }
  if (!receivedTerminal) onError("Audit stream closed before completing");
}

export function useGapAnalysisGenerate(conceptId: string) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const handleComplete = useCallback(
    (result: AuditResult) => {
      queryClient.setQueryData(["audit", conceptId], result);
      queryClient.invalidateQueries({ queryKey: ["audit", conceptId] });
      // Bridge: also write into the legacy gap-analysis-store so existing
      // consumers (analyze.tsx, GapAnalysisPanel) read fresh data without
      // needing migration to the React Query cache. Task 13 retires the
      // legacy store's run() but keeps state for read-side consumers.
      useGapAnalysisStore
        .getState()
        .setResult(conceptId, result as unknown as GapAnalysisResult);
      useGapAnalysisGenerationStore.getState().finish();
      toast.success("Audit ready");
    },
    [conceptId, queryClient],
  );

  const handleError = useCallback((msg: string) => {
    useGapAnalysisStore.getState().setError(msg);
    useGapAnalysisGenerationStore.getState().fail(msg);
    toast.error("Audit failed", { description: msg });
  }, []);

  const start = useCallback(
    async (opts: GenerateOptions = {}) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Optimistic preflight: surface progress immediately. start() spreads
      // initialState so we re-set cancelController right after.
      useGapAnalysisGenerationStore.getState().start(conceptId, "");
      useGapAnalysisGenerationStore.getState().setCancelController(controller);

      // Cancel any existing active job for this concept (cross-tab / stale).
      try {
        const active = await apiClient
          .get(
            `api/concepts/${conceptId}/clustering/coverage-audit/generate/active`,
            { timeout: 10_000 },
          )
          .json<ActiveJobResponse | null>();
        if (
          active &&
          (active.status === "pending" || active.status === "running")
        ) {
          await apiClient.post(
            `api/concepts/${conceptId}/clustering/coverage-audit/generate/${active.job_id}/cancel`,
            { timeout: 10_000 },
          );
        }
      } catch {
        // proceed — best-effort
      }
      if (controller.signal.aborted) return;

      try {
        const res = await apiClient
          .post(`api/concepts/${conceptId}/clustering/coverage-audit/generate`, {
            json: opts,
            timeout: 30_000,
          })
          .json<StartResponse>();
        if (controller.signal.aborted) return;

        // Re-arm: start() spreads initialState which wipes cancelController.
        useGapAnalysisGenerationStore.getState().start(conceptId, res.job_id);
        useGapAnalysisGenerationStore
          .getState()
          .setCancelController(controller);

        await consumeAuditStream(
          conceptId,
          res.job_id,
          controller.signal,
          handleComplete,
          handleError,
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        if ((err as { name?: string })?.name === "AbortError") return;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 409) {
          handleError("An audit is already in progress — wait or cancel it.");
          return;
        }
        handleError(err instanceof Error ? err.message : "Failed to start audit");
      }
    },
    [conceptId, handleComplete, handleError],
  );

  const cancel = useCallback(async () => {
    const { jobId, cancelController } =
      useGapAnalysisGenerationStore.getState();
    (cancelController ?? abortRef.current)?.abort();
    useGapAnalysisGenerationStore.getState().reset();
    if (!jobId) return;
    try {
      await apiClient.post(
        `api/concepts/${conceptId}/clustering/coverage-audit/generate/${jobId}/cancel`,
      );
    } catch {
      // best-effort
    }
  }, [conceptId]);

  // Resume on mount: if a job was running on this concept (e.g. user navigated
  // away mid-run), reattach the SSE stream.
  useEffect(() => {
    const controller = new AbortController();
    let attached = false;
    (async () => {
      try {
        const active = await apiClient
          .get(
            `api/concepts/${conceptId}/clustering/coverage-audit/generate/active`,
            { timeout: 10_000 },
          )
          .json<ActiveJobResponse | null>();
        if (!active || controller.signal.aborted) return;
        const store = useGapAnalysisGenerationStore.getState();
        if (store.jobId === active.job_id && store.isPending) return;
        attached = true;
        useGapAnalysisGenerationStore
          .getState()
          .start(conceptId, active.job_id);
        useGapAnalysisGenerationStore
          .getState()
          .setCancelController(controller);
        useGapAnalysisGenerationStore
          .getState()
          .setStep(
            (active.step ?? 0) as 0 | 1 | 2 | 3,
            active.step_label || STEP_FALLBACK_LABELS[active.step ?? 0] || "",
          );
        abortRef.current?.abort();
        abortRef.current = controller;
        await consumeAuditStream(
          conceptId,
          active.job_id,
          controller.signal,
          handleComplete,
          handleError,
        );
      } catch {
        // 404 / no active job — fine
      }
    })();
    return () => {
      if (!attached) controller.abort();
    };
  }, [conceptId, handleComplete, handleError]);

  return { start, cancel };
}
